import { createSelector } from 'reselect'
import _ from 'lodash'
import {
  getFormsList,
  getResidentsList,
  getUsersList,
  getRiskAssessmentList,
} from './Lists'
import { getRiskLevel, groupDependentFields } from '../common/utils'
import { formatWithResidentInfo } from './helpers/InitialAssessments/formatFieldOutput'
import { isDependentFieldHidden } from '../common/validation/functions'

const getAssessmentById = (state, { risk_assessment }) =>
  _.find(state.data['risk-assessments'], { id: risk_assessment })

const getResidentById = (state, { resident }) =>
  _.find(state.data.residents, { id: resident })

const getRiskAssessmentForm = (state, props) => {
  if (props && props.riskAssessment) {
    return props.riskAssessment
  }
  return state.forms.createRiskAssessment
}

const getRiskAssessmentSortType = (state) =>
  state.carePlans.riskAssessmentSortType

// Gets a user-generated form from the state based on the current router params.
export const getFormByParams = (state, props) => {
  const params = _.get(props, 'router.params')
  let form
  if (
    params &&
    (params.risk_assessment_history_id || params.risk_assessment_id)
  ) {
    const riskAssessment = _.find(state.data['risk-assessments'], {
      id: params.risk_assessment_history_id || params.risk_assessment_id,
    })
    form = riskAssessment ? riskAssessment.form : null
  } else if (params && params.pre_assessment_id) {
    const initialAssessment = _.find(state.data['initial-assessments'], {
      id: params.pre_assessment_id,
    })
    form = initialAssessment ? initialAssessment.form : null
  } else if (props.form_id) {
    form = _.find(state.data.forms, { id: props.form_id })
  }

  if (form && form.formFields && params && params.id) {
    const resident = _.find(getResidentsList(state), { id: params.id })
    if (resident) return formatLabelsWithResidentInfo(form, resident)
  }
  return form
}

export const getRiskAssessments = createSelector(
  [getUsersList, getRiskAssessmentList],
  (carers, riskAssessments) => {
    if (!carers || !riskAssessments) return null

    const carersById = _.keyBy(carers, 'id')
    return riskAssessments.map((row) => ({
      ...row,
      createdBy: row.createdBy && carersById[row.createdBy],
      editedBy: row.editedBy && carersById[row.editedBy],
    }))
  }
)

// Calculates the risk score and level for the current form
export const calculateAssessmentRisk = createSelector(
  [getFormByParams, getRiskAssessmentForm],
  (form, riskForm) => {
    if (!form || !riskForm) return null
    let level = 'incomplete'
    let score = 0

    const riskFields = form.formFields.filter(
      (field) => field.optionType === 'riskScore'
    )

    // only show risk result when every non-hidden risk field has been filled in
    if (isAllRiskFieldsFilled(riskFields, riskForm.fields)) {
      score = getRiskScore(riskFields, riskForm)
      level = getRiskLevel(form.ranges, score)
    }

    return { level, score }
  }
)

export const concatenateFormsAndAssessments = createSelector(
  [getFormsList, getRiskAssessmentList],
  (forms, riskAssessments) => {
    if (!forms || !riskAssessments) return null
    const formsWithoutAssessments = _.filter(forms, (form) => {
      if (form.isRevision) return false
      const assessmentForForm = _.find(riskAssessments, (assessment) => {
        if (assessment.form && assessment.form.name) {
          return form.formId === assessment.form.formId
        }
      })
      return !assessmentForForm
    })

    const allItems = [...formsWithoutAssessments, ...riskAssessments]

    return _.sortBy(allItems, (item) => {
      return item.form && item.form.name ? item.form.name : item.name
    })
  }
)

export const formatFieldsForAssessment = createSelector(
  [getFormByParams],
  (form) => {
    if (!form) return null

    const withKeys = _.map(form.formFields, formatFieldWithKeys)
    const formFields = groupDependentFields(withKeys)

    return { ...form, formFields }
  }
)

export const formatFieldsForAssessmentDisplay = createSelector(
  [getFormByParams],
  (form) => {
    if (!form) return null

    const withKeys = _.map(form.formFields, formatFieldWithKeys)
    const formFields = groupDependentFields(withKeys).map((field) => {
      if (field.dependentFields) {
        return {
          ...field,
          dependentFields: field.dependentFields.filter(
            (entry) => entry.type !== 'suggestedActions'
          ),
        }
      }
      return { ...field }
    })

    return { ...form, formFields }
  }
)

export const formatRiskAssessment = createSelector(
  [getAssessmentById, getResidentById],
  (riskAssessment, resident) => {
    if (!riskAssessment || !resident) return null
    return riskAssessment.form
      ? {
          ...riskAssessment,
          form: formatLabelsWithResidentInfo(riskAssessment.form, resident),
        }
      : riskAssessment
  }
)

export const formatRiskAssessmentPDF = createSelector(
  [getAssessmentById, getResidentById],
  (riskAssessment, resident) => {
    if (!riskAssessment || !resident) return null

    let formattedForm

    if (riskAssessment.form) {
      formattedForm = formatLabelsWithResidentInfo(
        riskAssessment.form,
        resident
      )
    }

    return riskAssessment.form
      ? {
          ...riskAssessment,
          form: {
            ...formattedForm,
            formFields: formattedForm.formFields.filter(
              (entry) => !entry.suggestedActions
            ),
          },
        }
      : riskAssessment
  }
)

export const sortRiskAssessments = createSelector(
  [getRiskAssessmentList, getRiskAssessmentSortType],
  (riskAssessments, sortType) => {
    if (!riskAssessments) return null
    switch (sortType) {
      case 'recent':
        return _.sortBy(riskAssessments, 'createdOn').reverse()
      case 'high-low':
        const noRiskLevel = _.filter(riskAssessments, hasNoRiskLevel)
        return _(riskAssessments)
          .reject(hasNoRiskLevel)
          .sortBy('riskScore')
          .sortBy(sortByRiskLevel)
          .reverse()
          .concat(noRiskLevel)
          .value()
      case 'low-high':
        return _(riskAssessments)
          .sortBy('riskScore')
          .sortBy(sortByRiskLevel)
          .value()
      case 'a-z':
        return _.sortBy(riskAssessments, 'form.name')
      default:
        return riskAssessments
    }
  }
)

/**
 * Calculates risk assessment score based on user-generated form specification
 * and current form data in state.
 *
 * @param {Object} fields the fields from the user-generated form specification,
 * built by the FormBuilder library. Contains non-LmC properties, such as
 * `values` instead of `options`
 * @param {Object} form the LmC form, which can be either a form object stored in
 * the database (e.g. a completed risk assessment), or an incomplete form state
 * derived from Redux.
 */
const getRiskScore = (fields, form) => {
  let score = 0
  const formFields = form.fields || form.formData

  for (let field of fields) {
    // Don't take hidden fields into account for score
    if (isFieldDependentAndHidden(field, formFields)) {
      continue
    }
    const formValue = _.get(formFields, field.key)

    if (!field.values || !field.values.length) {
      if (formValue || formValue === 0) {
        score += formValue
        continue
      }
    }

    const values = _.map(field.values, 'value')
    if (formValue || formValue === 0) {
      score +=
        formValue instanceof Array
          ? formValue.reduce((a, b) => a + values[b], 0)
          : values[formValue]
    }
  }
  return score
}

/**
 * Takes a form field specification generated by users with the FormBuilder
 * library, and returns a version with modified keys. The new keys reflect the
 * keys used elsewhere in the app for forms and form data, such as `options`
 * instead of `values`.
 *
 * @param {Object} field
 */
export const formatFieldWithKeys = (field) => {
  let formattedField = {
    ...field,
    keys: field.key,
    label: field.label,
    layout: 'no-label',
    required: true,
    type: field.type,
  }
  if (field.description) formattedField.note = field.description
  if (field.displayCalendar) formattedField.displayCalendar = true
  if (field.formFieldStyle) formattedField.formFieldStyle = field.formFieldStyle
  if (field.hasComment) formattedField.hasComment = true
  if (field.placeholder) formattedField.placeholders = field.placeholder
  if (field.style) formattedField.style = field.style
  if (field.validationTypes)
    formattedField.validationTypes = field.validationTypes
  if (field.values) formattedField.options = field.values
  if (field.dependsOnKey) {
    formattedField = {
      ...formattedField,
      dependsOn: {
        key: field.dependsOnKey,
        value: field.dependsOnValue,
      },
    }
  }
  if (field.suggestedActions) {
    formattedField.suggestedActions = field.suggestedActions
  }
  return formattedField
}

const formatLabelsWithResidentInfo = (form, resident) => ({
  ...form,
  formFields: form.formFields
    ? form.formFields.map((field) => ({
        ...field,
        label: formatWithResidentInfo(field.label, resident),
      }))
    : null,
})

const hasNoRiskLevel = (assessment) => !assessment.riskLevel

const isAllRiskFieldsFilled = (riskFields, formFields) => {
  for (let field of riskFields) {
    if (isFieldDependentAndHidden(field, formFields)) {
      continue
    }
    if (!_.has(formFields, field.key)) {
      return false
    }
  }
  return true
}

const isFieldDependentAndHidden = (field, allFields) =>
  field.dependsOnKey &&
  isDependentFieldHidden(allFields, {
    key: field.dependsOnKey,
    value: field.dependsOnValue,
  })

const sortByRiskLevel = (assessment) => {
  const riskLevels = {
    na: 1,
    low: 2,
    medium: 3,
    high: 4,
    severe: 5,
  }
  return riskLevels[assessment.riskLevel] || 0
}
